import { Component, Input } from '@angular/core';

/**
 * Which tier a highlight box belongs to: a light full-row band, a stronger field box on top, or a
 * `locked` band — a row-level box the host pinned rather than derived from a passing hover, drawn
 * stronger than `row` so it reads as held in place (WB-7483).
 */
export type HighlightVariant = 'row' | 'field' | 'locked';

/** A normalized box (fractions of page width/height, top-left origin). Kept local so this shared
 *  component has no dependency on the quotations feature; it is structurally compatible with
 *  `INormalizedBox` from the quotations models. */
export interface HighlightBox {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Optional tier; defaults to the strong (field-style) look when absent — backward compatible. */
  variant?: HighlightVariant;
}

export interface PxBox {
  left: number;
  top: number;
  width: number;
  height: number;
  variant?: HighlightVariant;
}

/** Pure mapping: normalized 0–1 box → on-screen pixels for a container of the given size. */
export function boxToPx(box: HighlightBox, width: number, height: number): PxBox {
  return {
    left: box.x * width,
    top: box.y * height,
    width: box.w * width,
    height: box.h * height,
    variant: box.variant,
  };
}

/**
 * Presentational overlay that draws highlight rectangles over a rendered document page/image.
 * The host positions this layer (offsetLeft/offsetTop/width/height) to exactly cover the target
 * element; the boxes are then placed within it by scaling their normalized coordinates.
 */
@Component({
  selector: 'app-document-highlight-layer',
  templateUrl: './document-highlight-layer.component.html',
  styleUrls: ['./document-highlight-layer.component.scss'],
})
export class DocumentHighlightLayerComponent {
  @Input() boxes: HighlightBox[] = [];
  /** Rendered size (px) of the target element this layer covers. */
  @Input() width = 0;
  @Input() height = 0;
  /** Position (px) of the target element relative to the positioned host. */
  @Input() offsetLeft = 0;
  @Input() offsetTop = 0;

  get pxBoxes(): PxBox[] {
    if (!this.width || !this.height) {
      return [];
    }
    return (this.boxes || []).map((b) => boxToPx(b, this.width, this.height));
  }

  /**
   * Stable identity per drawn box: without a trackBy, `pxBoxes`' fresh objects re-create every box
   * on every change-detection tick and the breathing glow (WB-7549) restarts at frame 0 forever.
   * `index` keeps keys unique (one row location can carry several boxes); variant + geometry change
   * the key when the highlight moves to another line, rounded so a sub-pixel re-measure doesn't.
   */
  trackByBox(index: number, box: PxBox): string {
    const px = (n: number) => Math.round(n);
    return `${index}:${box.variant ?? ''}:${px(box.left)}:${px(box.top)}:${px(box.width)}:${px(box.height)}`;
  }
}
